import React, { Suspense, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

// Import the components directly instead of using dynamic import
import { AIVoiceAgentDashboard } from '../../components/voice';

// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Voice Agent component failed:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-red-800 mb-2">Something went wrong</h2>
              <p className="text-red-700 mb-4">
                There was an error loading the Voice Agents dashboard.
              </p>
              <p className="text-sm text-red-600 mb-4">
                Error: {this.state.error?.message || "Unknown error"}
              </p>
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AIVoiceAgents: React.FC = () => {
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">LeadWealth.ai Voice Agents</h1>
        <p className="mt-1 text-gray-600">
          Manage your AI voice agents for automated client outreach
        </p>
      </div>
      
      <ErrorBoundary>
        <Suspense fallback={
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-primary animate-spin mr-3" />
            <span className="text-gray-600">Loading voice agent dashboard...</span>
          </div>
        }>
          <AIVoiceAgentDashboard />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

export default AIVoiceAgents;